import { ArrayPath } from './ArrayPath'

type ReducerToken = {
  inString: string
  acc: string
  escaped: boolean
  pathArray: ArrayPath
  possibleArrayIndex: boolean
  scopeEnders: { '/': boolean; '.': boolean; '[': boolean }
  useStringDelimiters: boolean
}

/**
 * Converts string arrays of different formats into path arrays.
 * @param path The path to convert
 * @param format The format to convert from. Can be "js" (`foo."b a r".baz[2]`), "jsonata" (`$.foo."b a r".baz[2]` or "json" (`#/foo/b a r/baz/2`) or "dot" (`foo.b a r.baz.2`) or "detect". Defaults to "detect"
 * @returns Array of numbers or strings
 */
export const getArrayPath = (
  path: string,
  format: 'js' | 'json' | 'jsonata' | 'dot' | 'detect' = 'detect'
) =>
  Array.from(
    path.startsWith('{{') && path.endsWith('}}')
      ? path.substring(2, path.length - 2)
      : path
  ).reduce(
    (token: ReducerToken, c, i, arr) => {
      if (i === arr.length - 1 && (c !== ']' || !token.scopeEnders['['])) {
        if (
          !(c === '#' && token.pathArray.length === 0) &&
          !(
            c === '$' &&
            token.pathArray.length === 0 &&
            format !== 'js' &&
            format !== 'json'
          )
        ) {
          if (c !== '"' && c !== "'") {
            token.acc += c
          }
          if (token.possibleArrayIndex) {
            const index = parseInt(token.acc)
            token.pathArray.push(isNaN(index) ? token.acc : index)
          } else {
            token.pathArray.push(token.acc)
          }
        }

        return token
      }

      if (token.escaped) {
        token.acc += c
        token.escaped = false

        return token
      }

      if (c === '\\') {
        token.escaped = true

        return token
      }

      if (c === token.inString) {
        token.inString = ''

        return token
      }

      if (
        !token.inString &&
        token.useStringDelimiters &&
        (c === '"' || c === "'" || c === '`')
      ) {
        token.inString = c

        return token
      }

      if (token.scopeEnders[c]) {
        if (c === '/') {
          token.scopeEnders['.'] = false
          token.scopeEnders['['] = false
          token.useStringDelimiters = false
        } else {
          token.scopeEnders['/'] = false
        }

        if (
          token.acc &&
          !(c === '/' && token.acc === '#' && !token.pathArray.length) &&
          !(
            (c === '.' || c === '[') &&
            token.acc === '$' &&
            format !== 'js' &&
            !token.pathArray.length
          )
        ) {
          if (token.possibleArrayIndex) {
            const index = parseInt(token.acc)
            token.pathArray.push(isNaN(index) ? token.acc : index)
          } else {
            token.pathArray.push(token.acc)
          }
        }

        token.possibleArrayIndex = true
        token.acc = ''

        return token
      }

      if (c === ']' && token.scopeEnders['[']) {
        if (token.possibleArrayIndex) {
          const index = parseInt(token.acc)
          token.pathArray.push(isNaN(index) ? token.acc : index)
        } else {
          token.pathArray.push(token.acc)
        }
        token.possibleArrayIndex = true
        token.acc = ''

        return token
      }

      token.possibleArrayIndex =
        token.possibleArrayIndex &&
        (c === '0' ||
          c === '1' ||
          c === '2' ||
          c === '3' ||
          c === '4' ||
          c === '5' ||
          c === '6' ||
          c === '7' ||
          c === '8' ||
          c === '9' ||
          (token.acc === '' && c === '-'))
      token.acc += c

      return token
    },
    {
      inString: '',
      acc: '',
      escaped: false,
      pathArray: [],
      possibleArrayIndex: true,
      scopeEnders: {
        '/': format !== 'js',
        '.': format !== 'json',
        '[': format !== 'json' && format !== 'dot',
      },
      useStringDelimiters: format !== 'json' && format !== 'dot',
    }
  ).pathArray
